import React, { useCallback, useEffect, useRef } from 'react';
import {
  DiscoveryProvider,
  useDiscoveryContext,
} from './context/DiscoveryContext';
import { useDiscoverySession, generateSessionId } from './hooks/useDiscoverySession';
import { useWireframe } from './hooks/useWireframe';
import ChatPanel from './components/ChatPanel';
import ProgressBar from './components/ProgressBar';
import './discovery.css';

function DiscoveryInner() {
  const { state, dispatch } = useDiscoveryContext();
  const session = useDiscoverySession();
  const initialized = useRef(false);
  const saveTimerRef = useRef(null);

  const { generate } = useWireframe();

  const debouncedSave = useCallback(
    (data) => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        session.saveSession(data);
      }, 300);
    },
    [session],
  );

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    dispatch({
      type: 'INIT_SESSION',
      payload: {
        sessionId: session.sessionId,
        messages: session.savedMessages,
        wireframes: session.savedWireframes,
        lead: session.savedLead,
      },
    });
  }, [
    dispatch,
    session.sessionId,
    session.savedMessages,
    session.savedWireframes,
    session.savedLead,
  ]);

  useEffect(() => {
    if (!state.sessionId) return;
    debouncedSave({
      sessionId: state.sessionId,
      messages: state.messages,
      wireframes: state.wireframes,
      lead: state.lead,
    });
  }, [state.sessionId, state.messages, state.wireframes, state.lead, debouncedSave]);

  useEffect(() => {
    const assistantCount = state.messages.filter(
      (m) => m.role === 'assistant',
    ).length;
    generate(state.messages);
  }, [state.messages, generate]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleReset = useCallback(() => {
    session.clearSession();
    dispatch({ type: 'RESET_SESSION' });
    const newId = generateSessionId();
    dispatch({
      type: 'INIT_SESSION',
      payload: {
        sessionId: newId,
        messages: [],
        wireframes: [],
        lead: null,
      },
    });
  }, [session, dispatch]);

  return (
    <div className="discovery-page" data-testid="discovery-page">
      <header className="discovery-header">
        <h1>Explore your idea with Z37</h1>
        <p>Tell us what you&apos;re building. We&apos;ll help you think it through.</p>
      </header>
      <ProgressBar currentStage={state.currentStage} />
      <div className="discovery-chat-section">
        <ChatPanel onReset={handleReset} />
      </div>
      <footer className="discovery-footer">
        Powered by Z37 · Zero to one. Then beyond.
      </footer>
    </div>
  );
}

export default function DiscoveryPage() {
  return (
    <DiscoveryProvider>
      <DiscoveryInner />
    </DiscoveryProvider>
  );
}
